// cspell:disable
/* eslint-disable */
import React, { useState, useRef, memo } from "react";
import { File as IzFile, Folder as IzFolder } from "@induz/sdk";
import "./AdvancedSearchModal.scss";

import folderIcon from "../../../../assets/images/homepage/ySafe/tableIcons/folder.svg";
import pdfIcon from "../../../../assets/images/homepage/ySafe/tableIcons/pdf.svg";
import xlsIcon from "../../../../assets/images/homepage/ySafe/tableIcons/xls.svg";
import docsIcon from "assets/images/fileImage/docs.svg";
import idocIcon from "assets/images/fileImage/idoc.svg";
import isheetIcon from "assets/images/fileImage/isheet.png";
import jpgIcon from "assets/images/fileImage/jpg.svg";
import zipIcon from "assets/images/homepage/ySafe/zip.svg";
import commonIcon from "assets/images/homepage/ySafe/commonFile.svg";

// ─── MIME type options for the dropdown ──────────────────
const MIME_OPTIONS = [
    { value: "application/pdf", label: "PDF" },
    { value: "application/idoc", label: "iDoc" },
    { value: "application/isheet", label: "iSheet" },
    { value: "image/jpeg", label: "JPEG Image" },
    { value: "image/png", label: "PNG Image" },
    { value: "image/gif", label: "GIF Image" },
    { value: "image/svg+xml", label: "SVG Image" },
    { value: "image/webp", label: "WebP Image" },
    { value: "video/mp4", label: "MP4 Video" },
    { value: "video/webm", label: "WebM Video" },
    { value: "video/quicktime", label: "MOV Video" },
    { value: "audio/mpeg", label: "MP3 Audio" },
    { value: "audio/wav", label: "WAV Audio" },
    { value: "audio/ogg", label: "OGG Audio" },
    { value: "text/plain", label: "Plain Text" },
    { value: "text/csv", label: "CSV" },
    { value: "application/zip", label: "ZIP Archive" },
    { value: "application/msword", label: "DOC" },
    { value: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", label: "DOCX" },
    { value: "application/vnd.ms-excel", label: "XLS" },
    { value: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", label: "XLSX" },
];

const SIZE_UNITS = [
    { label: "B", mult: 1 },
    { label: "KB", mult: 1024 },
    { label: "MB", mult: 1048576 },
    { label: "GB", mult: 1073741824 },
];

function convertUTCToLocal(dateString) {
    if (!dateString) return "";
    const isoUTC = dateString.replace(" ", "T") + "Z";
    const d = new Date(isoUTC);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: false,
    }).replace(",", "");
}

const getFileIcon = (fileName, type) => {
    if (type === "folder") return folderIcon;
    const ext = fileName?.split(".").pop().toLowerCase();
    switch (ext) {
        case "xls": case "xlsx": case "csv": return xlsIcon;
        case "pdf": return pdfIcon;
        case "doc": case "docx": return docsIcon;
        case "idoc": return idocIcon;
        case "isheet": return isheetIcon;
        case "jpg": case "png": case "jpeg": case "svg": return jpgIcon;
        case "zip": return zipIcon;
        default: return commonIcon;
    }
};

// ─── Build SDK regexOptions from the new filter state ───
function buildRegexOptions({ selectedMimes, sizeRanges, dateRanges, searchName, folderPath }) {
    const opts = {};

    // fileType → IGenericFlag[] { name: mime, flag: true }
    if (selectedMimes.length > 0) {
        opts.fileType = selectedMimes.map((mime) => ({ name: mime, flag: true }));
    }

    // size → IRange[] { minVal, maxVal } in bytes
    const validSizes = sizeRanges.filter((r) => r.minVal !== "" || r.maxVal !== "");
    if (validSizes.length > 0) {
        opts.size = validSizes.map((r) => {
            const range = {};
            if (r.minVal !== "") range.minVal = parseFloat(r.minVal) * r.minUnit;
            if (r.maxVal !== "") range.maxVal = parseFloat(r.maxVal) * r.maxUnit;
            return range;
        });
    }

    // time → IRange[] { minVal, maxVal } in unix seconds
    const validDates = dateRanges.filter((r) => r.from || r.to);
    if (validDates.length > 0) {
        opts.time = validDates.map((r) => {
            const range = {};
            if (r.from) range.minVal = Math.floor(new Date(r.from).getTime() / 1000);
            if (r.to) range.maxVal = Math.floor(new Date(r.to).getTime() / 1000);
            return range;
        });
    }

    // regex → path%name string
    folderPath = folderPath.replace(/%/g, "\\%") + (folderPath.endsWith("/") ? "" : "/");

    if (searchName) {
        opts.regex = folderPath + "%" + searchName;
    }

    return Object.keys(opts).length > 0 ? opts : null;
}

// ─── Map SDK items to display objects ───────────────────
function mapItems(list) {
    return list.map((item) => {
        if (item instanceof IzFolder) {
            const parentPath = item.getParentPath?.() || "";
            const folderName = item.getFolderName();
            const fullPath = parentPath ? `${parentPath}/${folderName}` : `/${folderName}`;
            return { name: folderName, type: "folder", size: "", modified: convertUTCToLocal(item.getLastModifiedDateString()), owner: item.getOwner(), parentPath, fullPath: fullPath.replace(/\/+/g, "/") };
        } else if (item instanceof IzFile) {
            const parentPath = item.getParentPath?.() || "";
            const fileName = item.getName();
            const fullPath = parentPath ? `${parentPath}/${fileName}` : `/${fileName}`;
            return { name: fileName, type: "file", size: item.getSizeString(), modified: convertUTCToLocal(item.getLastModifiedDateString()), owner: item.getOwner(), parentPath, fullPath: fullPath.replace(/\/+/g, "/") };
        }
        return null;
    }).filter(Boolean);
}

// ─── Small sub-component: multi-select MIME dropdown ────
function MimeDropdown({ selected, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const toggle = (mime) => {
        onChange(selected.includes(mime) ? selected.filter((m) => m !== mime) : [...selected, mime]);
    };

    return (
        <div className="adv-mime-dropdown" ref={ref}>
            <button className="adv-mime-trigger" onClick={() => setOpen(!open)} type="button">
                {selected.length === 0
                    ? "Select file types…"
                    : `${selected.length} type${selected.length > 1 ? "s" : ""} selected`}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d={open ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            {open && (
                <div className="adv-mime-list">
                    {MIME_OPTIONS.map((opt) => (
                        <label key={opt.value} className={`adv-mime-option ${selected.includes(opt.value) ? "checked" : ""}`}>
                            <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)} />
                            <span className="adv-mime-label">{opt.label}</span>
                            <span className="adv-mime-value">{opt.value}</span>
                        </label>
                    ))}
                </div>
            )}
            {selected.length > 0 && (
                <div className="adv-mime-tags">
                    {selected.map((mime) => {
                        const label = MIME_OPTIONS.find((o) => o.value === mime)?.label || mime;
                        return (
                            <span key={mime} className="adv-mime-tag">
                                {label}
                                <button onClick={() => toggle(mime)} type="button">×</button>
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─── Component ──────────────────────────────────────────
function AdvancedSearchModal({ isOpen, onClose, currentPath }) {
    // Filter state
    const [searchName, setSearchName] = useState("");
    const [selectedMimes, setSelectedMimes] = useState([]);
    const [sizeRanges, setSizeRanges] = useState([{ minVal: "0", minUnit: 1048576, maxVal: "", maxUnit: 1048576 }]);
    const [dateRanges, setDateRanges] = useState([{ from: "", to: "" }]);

    // Results state
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searched, setSearched] = useState(false);

    // Pagination state
    const folderRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [fetching, setFetching] = useState(false);

    if (!isOpen) return null;

    // ─── Size range helpers ─────────────────────────────
    const updateSizeRange = (idx, field, value) => {
        setSizeRanges((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
    };
    const addSizeRange = () => setSizeRanges((prev) => [...prev, { minVal: "", minUnit: 1048576, maxVal: "", maxUnit: 1048576 }]);
    const removeSizeRange = (idx) => setSizeRanges((prev) => prev.filter((_, i) => i !== idx));

    // ─── Date range helpers ─────────────────────────────
    const updateDateRange = (idx, field, value) => {
        setDateRanges((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
    };
    const addDateRange = () => setDateRanges((prev) => [...prev, { from: "", to: "" }]);
    const removeDateRange = (idx) => setDateRanges((prev) => prev.filter((_, i) => i !== idx));

    const hasAnyFilter = searchName.trim() !== ""
        || selectedMimes.length > 0
        || sizeRanges.some((r) => r.minVal !== "" || r.maxVal !== "")
        || dateRanges.some((r) => r.from || r.to);

    const handleSearch = async () => {
        setSearching(true);
        setSearched(false);
        setResults([]);
        setCurrentPage(1);
        setTotalPages(1);
        setTotalItems(0);
        folderRef.current = null;

        try {
            const formattedPath = !currentPath || currentPath.length === 0
                ? "/" : `/${currentPath.join("/")}`;

            const fo = new IzFolder();
            fo.pageSize = 50;
            let folder;
            if (formattedPath === "/" || formattedPath === "") {
                folder = fo.getRoot();
            } else {
                folder = fo.folderWithName(formattedPath);
            }
            folder.pageSize = 50;

            const regexOpts = buildRegexOptions({ selectedMimes, sizeRanges, dateRanges, searchName: searchName.trim(), folderPath: formattedPath });
            // console.log("regexOptions:", regexOpts);
            const list = await folder.getFolderList(0, regexOpts);

            folderRef.current = folder;
            const total = folder.getTotalItems();
            const pages = Math.ceil(total / folder.getPageSize());
            setTotalItems(total);
            setTotalPages(pages || 1);
            setCurrentPage(1);
            setResults(mapItems(list));
        } catch (error) {
            console.error("Advanced search error:", error);
            setResults([]);
        } finally {
            setSearching(false);
            setSearched(true);
        }
    };

    const handleNextPage = async () => {
        if (!folderRef.current || currentPage >= totalPages || fetching) return;
        setFetching(true);
        try {
            const list = await folderRef.current.getNextFolderList(currentPage);
            setCurrentPage((p) => p + 1);
            setResults(mapItems(list));
        } catch (err) {
            console.error("Pagination error:", err);
        } finally {
            setFetching(false);
        }
    };

    const handlePrevPage = async () => {
        if (!folderRef.current || currentPage <= 1 || fetching) return;
        setFetching(true);
        try {
            const list = await folderRef.current.getPreviousFolderList(currentPage - 1);
            setCurrentPage((p) => Math.max(p - 1, 1));
            setResults(mapItems(list));
        } catch (err) {
            console.error("Pagination error:", err);
        } finally {
            setFetching(false);
        }
    };

    const handleFolderDoubleClick = (item) => {
        const parts = item.fullPath.split("/").filter(Boolean);
        window.dispatchEvent(new CustomEvent("advSearchNavigate", { detail: parts }));
        handleClose();
    };

    const handleFileDoubleClick = (item) => {
        window.dispatchEvent(new CustomEvent("advSearchOpenFile", {
            detail: { fullPath: item.fullPath, parentPath: item.parentPath, fileName: item.name, owner: item.owner }
        }));
        handleClose();
    };

    const handleClear = () => {
        setSearchName("");
        setSelectedMimes([]);
        setSizeRanges([{ minVal: "", minUnit: 1048576, maxVal: "", maxUnit: 1048576 }]);
        setDateRanges([{ from: "", to: "" }]);
        setResults([]);
        setSearched(false);
        setCurrentPage(1);
        setTotalPages(1);
        setTotalItems(0);
        folderRef.current = null;
    };

    const handleClose = () => {
        handleClear();
        onClose();
        window.dispatchEvent(new CustomEvent("advSearchClosed"));
    };

    const pathLabel = !currentPath || currentPath.length === 0 ? "/" : "/" + currentPath.join("/");
    const pageSize = folderRef.current ? folderRef.current.getPageSize() : 50;
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = currentPage === totalPages ? totalItems : currentPage * pageSize;

    return (
        <div className="adv-search-overlay" onClick={handleClose}>
            <div className="adv-search-modal" onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className="adv-search-header">
                    <div className="adv-search-header-left">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                        </svg>
                        <span className="adv-search-title">Advanced Search</span>
                        <span className="adv-search-path">{pathLabel}</span>
                    </div>
                    <button className="adv-search-close" onClick={handleClose}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M18 6L6 18" /><path d="M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="adv-search-body">

                    {/* LEFT: Filters */}
                    <div className="adv-search-sidebar">

                        {/* Name */}
                        <div className="adv-filter-group">
                            <label className="adv-filter-label">Name</label>
                            <input
                                type="text"
                                className="adv-name-input"
                                placeholder="Search by file or folder name…"
                                value={searchName}
                                onChange={(e) => setSearchName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter" && hasAnyFilter) handleSearch(); }}
                            />
                        </div>

                        {/* File Type (MIME dropdown) */}
                        <div className="adv-filter-group">
                            <label className="adv-filter-label">File Type</label>
                            <MimeDropdown selected={selectedMimes} onChange={setSelectedMimes} />
                        </div>

                        {/* Size ranges */}
                        <div className="adv-filter-group">
                            <div className="adv-filter-label-row">
                                <label className="adv-filter-label">Size</label>
                                <button className="adv-add-btn" onClick={addSizeRange} type="button" title="Add size range">+</button>
                            </div>
                            {sizeRanges.map((r, idx) => (
                                <div key={idx} className="adv-range-row">
                                    <input type="number" className="adv-range-input" placeholder="Min" min="0" value={r.minVal} onChange={(e) => updateSizeRange(idx, "minVal", e.target.value)} />
                                    <select className="adv-unit-select" value={r.minUnit} onChange={(e) => updateSizeRange(idx, "minUnit", Number(e.target.value))}>
                                        {SIZE_UNITS.map((u) => <option key={u.label} value={u.mult}>{u.label}</option>)}
                                    </select>
                                    <span className="adv-range-sep">–</span>
                                    <input type="number" className="adv-range-input" placeholder="Max" min="0" value={r.maxVal} onChange={(e) => updateSizeRange(idx, "maxVal", e.target.value)} />
                                    <select className="adv-unit-select" value={r.maxUnit} onChange={(e) => updateSizeRange(idx, "maxUnit", Number(e.target.value))}>
                                        {SIZE_UNITS.map((u) => <option key={u.label} value={u.mult}>{u.label}</option>)}
                                    </select>
                                    {sizeRanges.length > 1 && (
                                        <button className="adv-remove-btn" onClick={() => removeSizeRange(idx)} type="button" title="Remove">×</button>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Date ranges */}
                        <div className="adv-filter-group">
                            <div className="adv-filter-label-row">
                                <label className="adv-filter-label">Modified Date</label>
                                <button className="adv-add-btn" onClick={addDateRange} type="button" title="Add date range">+</button>
                            </div>
                            {dateRanges.map((r, idx) => (
                                <div key={idx} className="adv-date-range-row">
                                    <div className="adv-date-pair">
                                        <span className="adv-date-label">From</span>
                                        <input type="datetime-local" className="adv-date-input" value={r.from} onChange={(e) => updateDateRange(idx, "from", e.target.value)} />
                                    </div>
                                    <div className="adv-date-pair">
                                        <span className="adv-date-label">To</span>
                                        <input type="datetime-local" className="adv-date-input" value={r.to} onChange={(e) => updateDateRange(idx, "to", e.target.value)} />
                                    </div>
                                    {dateRanges.length > 1 && (
                                        <button className="adv-remove-btn" onClick={() => removeDateRange(idx)} type="button" title="Remove">×</button>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="adv-filter-actions">
                            <button className="adv-btn-clear" onClick={handleClear} disabled={!hasAnyFilter && !searched}>Clear</button>
                            <button className="adv-btn-search" onClick={handleSearch} disabled={!hasAnyFilter || searching}>
                                {searching ? "Searching…" : "Search"}
                            </button>
                        </div>
                    </div>

                    {/* RIGHT: Results */}
                    <div className="adv-search-results">
                        {!searched && !searching && (
                            <div className="adv-results-empty">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-grey-weak, #666)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                                </svg>
                                <p>Select filters and search</p>
                            </div>
                        )}
                        {searching && (
                            <div className="adv-results-empty">
                                <div className="adv-spinner" /><p>Searching…</p>
                            </div>
                        )}
                        {searched && !searching && results.length === 0 && (
                            <div className="adv-results-empty">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-grey-weak, #666)" strokeWidth="1.2">
                                    <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" strokeLinecap="round" /><path d="M8 11h6" strokeLinecap="round" />
                                </svg>
                                <p>No results found</p>
                            </div>
                        )}
                        {!searching && results.length > 0 && (
                            <>
                                <div className="adv-results-list">
                                    {results.map((item, idx) => (
                                        <div key={idx} className="adv-result-row" onDoubleClick={() => item.type === "folder" ? handleFolderDoubleClick(item) : handleFileDoubleClick(item)}>
                                            <span className="adv-result-icon"><img src={getFileIcon(item.name, item.type)} alt="" className="adv-result-icon-img" /></span>
                                            <div className="adv-result-detail">
                                                <span className="adv-result-name" title={item.name}>{item.name}</span>
                                                <div className="adv-result-meta">
                                                    <span className="adv-result-path" title={item.parentPath}>{item.parentPath || "/"}</span>
                                                    {item.size && <span>{item.size}</span>}
                                                    {item.modified && <span>{item.modified}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="adv-pagination">
                                    <span className="adv-pagination-info">{startItem}–{Math.min(endItem, totalItems)} of {totalItems}</span>
                                    <span className="adv-pagination-page">Page {currentPage} of {totalPages}</span>
                                    <div className="adv-pagination-arrows">
                                        <button className="adv-pagination-btn" onClick={handlePrevPage} disabled={fetching || currentPage <= 1} aria-label="Previous page">
                                            <svg viewBox="0 0 24 24" width="18" height="18"><path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        </button>
                                        <button className="adv-pagination-btn" onClick={handleNextPage} disabled={fetching || currentPage >= totalPages} aria-label="Next page">
                                            <svg viewBox="0 0 24 24" width="18" height="18"><path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default memo(AdvancedSearchModal);
